/**
 * NOS-183: Side-by-side import review — source screenshot vs imported render.
 *
 * Full-screen overlay opened from the ImportReviewPanel. Left pane: the
 * page's source screenshot (captured by crawl-site, hosted in the
 * import-assets bucket); right pane: the imported render (InlinePreview, the
 * same component the builder canvas uses). Scroll is proportionally synced
 * between the panes. Pages without a stored screenshot degrade to the render
 * pane only.
 *
 * Per-section actions (pending sections only — content still carrying
 * import_source; approved sections are never touched):
 *   * Approve (default): strips import_source, exactly like the panel's
 *     "mark reviewed" — the section drops out of the pending review.
 *   * Redo with hint…: free-text hint + the section's crawled source content
 *     go through rewrite-section (import_context payload); the result
 *     replaces the section content in the PENDING import only. Redo history
 *     is bounded: the last pre-redo content is kept for one-step undo, and a
 *     subsequent redo overwrites that slot (no infinite stacking).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Check, Loader2, RotateCcw, Undo2, X } from "lucide-react"
import { supabase } from "@/lib/supabase"
import { useCustomizationStore } from "@/stores/customizationStore"
import { rewriteImportedSection } from "@/hooks/useImportJob"
import type { SectionWithPage } from "@/hooks/useImportReviewSections"
import type { Organization, PageSection } from "@/templates/types"
import type { Json } from "@/types/database"
import { InlinePreview } from "./InlinePreview"

export interface ImportCompareViewProps {
  /** Pending review sections (all pages) — drives the page rollup */
  sections: SectionWithPage[]
  /** slug → hosted source screenshot URL (from the job result) */
  screenshots: Record<string, string>
  orgId: string
  /** Import job id — redo-with-hint grounds the rewrite in its crawl data */
  jobId: string | null
  /** Page slug to open on */
  initialSlug?: string | null
  onClose: () => void
  /** Called after approve/redo so the panel refreshes its pending list */
  onRefresh: () => void
}

interface PageOption {
  slug: string
  title: string
  pending: number
}

/** Rollup of pending sections into page options (preserves panel ordering). */
export function pageOptionsFromSections(sections: SectionWithPage[]): PageOption[] {
  const bySlug = new Map<string, PageOption>()
  for (const item of sections) {
    const existing = bySlug.get(item.pageSlug)
    if (existing) {
      existing.pending++
    } else {
      bySlug.set(item.pageSlug, {
        slug: item.pageSlug,
        title: item.pageTitle ?? item.pageSlug,
        pending: 1,
      })
    }
  }
  return [...bySlug.values()]
}

export function ImportCompareView({
  sections,
  screenshots,
  orgId,
  jobId,
  initialSlug = null,
  onClose,
  onRefresh,
}: ImportCompareViewProps) {
  const org = useCustomizationStore((s) => s.org)
  const customizations = useCustomizationStore((s) => s.customizations)
  const pages = useCustomizationStore((s) => s.pages)

  const pageOptions = useMemo(() => pageOptionsFromSections(sections), [sections])
  const [slug, setSlug] = useState<string>(
    initialSlug && pageOptions.some((p) => p.slug === initialSlug)
      ? initialSlug
      : pageOptions[0]?.slug ?? ""
  )
  const [pageSections, setPageSections] = useState<PageSection[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
  // Bounded redo history: ONE pre-redo snapshot per section (last result kept)
  const [redoHistory, setRedoHistory] = useState<Record<string, Record<string, unknown>>>({})
  const [hintOpen, setHintOpen] = useState(false)
  const [hint, setHint] = useState("")
  const [busy, setBusy] = useState<"approve" | "redo" | "undo" | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const leftRef = useRef<HTMLDivElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)
  const syncingRef = useRef<"left" | "right" | null>(null)

  const screenshotUrl = screenshots[slug]
  const pageId = pages.find((p) => p.slug === slug)?.id ?? null
  const pendingIds = useMemo(
    () => new Set(sections.filter((s) => s.pageSlug === slug).map((s) => s.section.id)),
    [sections, slug]
  )

  // Full section list for the selected page — the render must show the whole
  // page (already-approved sections included), not just the pending ones.
  const loadPageSections = useCallback(async () => {
    if (!pageId) {
      // Page not in the org's page list (edge case) — fall back to pending only
      setPageSections(sections.filter((s) => s.pageSlug === slug).map((s) => s.section))
      return
    }
    setLoading(true)
    const { data, error } = await supabase
      .from("page_sections")
      .select("id, section_type, order_index, content, page_id, org_id, is_visible")
      .eq("org_id", orgId)
      .eq("page_id", pageId)
      .order("order_index", { ascending: true })
    if (!error && data) {
      setPageSections(data as unknown as PageSection[])
    }
    setLoading(false)
  }, [pageId, orgId, slug, sections])

  useEffect(() => {
    setSelectedSectionId(null)
    setHintOpen(false)
    setHint("")
    setActionError(null)
    void loadPageSections()
  }, [loadPageSections])

  // Proportional scroll sync — best-effort ("where feasible"): matching by
  // ratio, since the screenshot and the re-themed render never share exact
  // heights. The syncingRef guard breaks the feedback loop.
  const syncScroll = useCallback((from: "left" | "right") => {
    const source = from === "left" ? leftRef.current : rightRef.current
    const target = from === "left" ? rightRef.current : leftRef.current
    if (!source || !target) return
    if (syncingRef.current && syncingRef.current !== from) return
    syncingRef.current = from
    const sourceMax = source.scrollHeight - source.clientHeight
    const targetMax = target.scrollHeight - target.clientHeight
    if (sourceMax > 0 && targetMax > 0) {
      target.scrollTop = (source.scrollTop / sourceMax) * targetMax
    }
    window.requestAnimationFrame(() => {
      syncingRef.current = null
    })
  }, [])

  const handleSectionClick = useCallback(
    (sectionId: string) => {
      // Nav/announcement pseudo-sections aren't reviewable rows
      if (sectionId.startsWith("__")) return
      setSelectedSectionId(sectionId)
      setHintOpen(false)
      setHint("")
      setActionError(null)
    },
    []
  )

  const selectedSection = pageSections.find((s) => s.id === selectedSectionId) ?? null
  const selectedIsPending = selectedSection != null && pendingIds.has(selectedSection.id)

  /** Persist new content for a section (DB) and patch the local render. */
  const persistContent = useCallback(
    async (sectionId: string, content: Record<string, unknown>): Promise<boolean> => {
      const { error } = await supabase
        .from("page_sections")
        .update({ content: content as Json })
        .eq("id", sectionId)
        .eq("org_id", orgId)
      if (error) return false
      setPageSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, content } : s))
      )
      return true
    },
    [orgId]
  )

  const handleApprove = useCallback(async () => {
    if (!selectedSection || !selectedIsPending || busy) return
    setBusy("approve")
    setActionError(null)
    const content = { ...((selectedSection.content as Record<string, unknown>) ?? {}) }
    delete content.import_source
    const ok = await persistContent(selectedSection.id, content)
    setBusy(null)
    if (!ok) {
      setActionError("Couldn't save the approval. Please try again.")
      return
    }
    setSelectedSectionId(null)
    onRefresh()
  }, [selectedSection, selectedIsPending, busy, persistContent, onRefresh])

  const handleRedo = useCallback(async () => {
    if (!selectedSection || !selectedIsPending || busy || hint.trim() === "") return
    setBusy("redo")
    setActionError(null)
    const before = { ...((selectedSection.content as Record<string, unknown>) ?? {}) }
    const res = await rewriteImportedSection({
      sectionId: selectedSection.id,
      orgId,
      hint: hint.trim(),
      jobId,
      pageSlug: slug,
    })
    if (!res.ok || !res.content) {
      setBusy(null)
      setActionError(res.error ?? "Rewrite failed. Please try again.")
      return
    }
    // Merge over the existing content: internal keys (_styles, _name,
    // import_source, _detected_form) survive even if the model dropped them.
    const next = { ...before, ...res.content, import_source: before.import_source }
    const ok = await persistContent(selectedSection.id, next)
    setBusy(null)
    if (!ok) {
      setActionError("The rewrite succeeded but couldn't be saved. Please try again.")
      return
    }
    // Bounded history: keep only the LAST pre-redo content per section
    setRedoHistory((prev) => ({ ...prev, [selectedSection.id]: before }))
    setHintOpen(false)
    setHint("")
    onRefresh()
  }, [selectedSection, selectedIsPending, busy, hint, orgId, jobId, slug, persistContent, onRefresh])

  const handleUndoRedo = useCallback(async () => {
    if (!selectedSection || busy) return
    const before = redoHistory[selectedSection.id]
    if (!before) return
    setBusy("undo")
    setActionError(null)
    const ok = await persistContent(selectedSection.id, before)
    setBusy(null)
    if (!ok) {
      setActionError("Couldn't restore the previous version. Please try again.")
      return
    }
    setRedoHistory((prev) => {
      const next = { ...prev }
      delete next[selectedSection.id]
      return next
    })
    onRefresh()
  }, [selectedSection, busy, redoHistory, persistContent, onRefresh])

  const currentOption = pageOptions.find((p) => p.slug === slug)

  return (
    <div style={OVERLAY_STYLE} role="dialog" aria-label="Side-by-side import review">
      <div style={HEADER_STYLE}>
        <span style={TITLE_STYLE}>Compare with your old site</span>
        <select
          aria-label="Page"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          style={PAGE_SELECT_STYLE}
        >
          {pageOptions.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.title} ({p.pending} to review)
            </option>
          ))}
        </select>
        {currentOption && !screenshotUrl && (
          <span style={NO_SHOT_STYLE}>
            No source snapshot for this page — showing the imported render only.
          </span>
        )}
        <button type="button" onClick={onClose} style={CLOSE_BTN_STYLE} aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <div style={BODY_STYLE}>
        {screenshotUrl && (
          <div
            ref={leftRef}
            onScroll={() => syncScroll("left")}
            style={PANE_STYLE}
            data-testid="compare-source-pane"
          >
            <div style={PANE_LABEL_STYLE}>Your old site</div>
            <img
              src={screenshotUrl}
              alt={`Source screenshot of ${currentOption?.title ?? slug}`}
              style={{ width: "100%", display: "block" }}
            />
          </div>
        )}
        <div
          ref={rightRef}
          onScroll={() => syncScroll("right")}
          style={{ ...PANE_STYLE, borderLeft: screenshotUrl ? "1px solid rgba(255,255,255,0.1)" : "none" }}
          data-testid="compare-render-pane"
        >
          <div style={PANE_LABEL_STYLE}>Your imported page</div>
          {loading ? (
            <div style={{ padding: 24, color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
              Loading…
            </div>
          ) : (
            <InlinePreview
              org={org as Organization | null}
              customizations={customizations}
              sections={pageSections}
              pages={pages}
              selectedSectionId={selectedSectionId}
              onSectionClick={(id) => handleSectionClick(id)}
            />
          )}
        </div>
      </div>

      {/* Per-section action bar — pending sections only */}
      {selectedSection && selectedIsPending && (
        <div style={ACTION_BAR_STYLE}>
          <span style={{ color: "rgba(255,255,255,0.75)", marginRight: "auto" }}>
            {String(
              (selectedSection.content as Record<string, unknown>)?._name ??
                selectedSection.section_type.replace(/_/g, " ")
            )}
          </span>
          {actionError && <span style={{ color: "#f87171" }}>{actionError}</span>}
          {redoHistory[selectedSection.id] && (
            <button
              type="button"
              onClick={() => void handleUndoRedo()}
              disabled={busy != null}
              style={SECONDARY_BTN_STYLE}
            >
              <Undo2 size={12} />
              {busy === "undo" ? "Restoring…" : "Undo redo"}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setHintOpen((v) => !v)
              setActionError(null)
            }}
            disabled={busy != null}
            style={SECONDARY_BTN_STYLE}
          >
            <RotateCcw size={12} />
            Redo with hint…
          </button>
          <button
            type="button"
            onClick={() => void handleApprove()}
            disabled={busy != null}
            style={PRIMARY_BTN_STYLE}
          >
            {busy === "approve" ? (
              <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
            ) : (
              <Check size={12} />
            )}
            Approve
          </button>
        </div>
      )}

      {/* Redo hint input */}
      {selectedSection && selectedIsPending && hintOpen && (
        <div style={HINT_ROW_STYLE}>
          <textarea
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder="What should be different? e.g. “Keep the full mission statement from the original” or “This lost the list of programs”"
            aria-label="Redo hint"
            rows={2}
            style={HINT_INPUT_STYLE}
          />
          <button
            type="button"
            onClick={() => void handleRedo()}
            disabled={busy != null || hint.trim() === ""}
            style={{
              ...PRIMARY_BTN_STYLE,
              opacity: busy != null || hint.trim() === "" ? 0.6 : 1,
            }}
          >
            {busy === "redo" ? (
              <>
                <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} />
                Redoing…
              </>
            ) : (
              "Redo section"
            )}
          </button>
        </div>
      )}
    </div>
  )
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 200,
  background: "rgba(12,12,16,0.98)",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'DM Sans', sans-serif",
}

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 16px",
  borderBottom: "1px solid rgba(255,255,255,0.1)",
}

const TITLE_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "rgba(255,255,255,0.9)",
}

const PAGE_SELECT_STYLE: React.CSSProperties = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  color: "rgba(255,255,255,0.9)",
  fontSize: 12,
  padding: "5px 8px",
  fontFamily: "'DM Sans', sans-serif",
}

const NO_SHOT_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: "rgba(255,255,255,0.5)",
}

const CLOSE_BTN_STYLE: React.CSSProperties = {
  marginLeft: "auto",
  background: "none",
  border: "none",
  color: "rgba(255,255,255,0.6)",
  cursor: "pointer",
  padding: 4,
}

const BODY_STYLE: React.CSSProperties = {
  flex: 1,
  display: "flex",
  minHeight: 0,
}

const PANE_STYLE: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  background: "#fff",
  position: "relative",
}

const PANE_LABEL_STYLE: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  padding: "4px 10px",
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  color: "rgba(255,255,255,0.85)",
  background: "rgba(12,12,16,0.85)",
}

const ACTION_BAR_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  borderTop: "1px solid rgba(255,255,255,0.1)",
  fontSize: 12,
  background: "rgba(12,12,16,0.98)",
}

const PRIMARY_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 14px",
  background: "rgba(34,197,94,0.35)",
  border: "1px solid rgba(34,197,94,0.6)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
}

const SECONDARY_BTN_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "7px 12px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  color: "rgba(255,255,255,0.85)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
}

const HINT_ROW_STYLE: React.CSSProperties = {
  display: "flex",
  gap: 8,
  padding: "0 16px 12px",
  background: "rgba(12,12,16,0.98)",
}

const HINT_INPUT_STYLE: React.CSSProperties = {
  flex: 1,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 8,
  color: "rgba(255,255,255,0.9)",
  fontSize: 12,
  padding: "8px 10px",
  resize: "vertical" as const,
  fontFamily: "'DM Sans', sans-serif",
}
